// frontend/src/components/Navbar.jsx
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Trophy, ChevronDown, PlusCircle, Settings, Compass, Star } from "lucide-react";

import NotificationBell from "./NotificationBell";
import AnimeHouseWordmark from "./brand/AnimeHouseWordmark";
import { useCreateClubModal } from "./Clubs/createClub/CreateClubModalContext";
import { fetchMyClubs } from "../services/clubsApi";
import {
  APP_ROUTES,
  getMainNavItems,
  getCollapsibleSections,
  getMobilePrimaryTabs,
} from "../navigation/sidebarNavConfig";

/** Scrollable region: no visible scrollbar (wheel / touch still scroll). */
const SCROLL_NO_BAR =
  "overflow-y-auto overflow-x-hidden " +
  "[scrollbar-width:none] [-ms-overflow-style:none] " +
  "[&::-webkit-scrollbar]:hidden";

/** Joined-clubs list only: bounded height, internal scroll, hidden scrollbar. */
const CLUB_LIST_SCROLL =
  "min-h-0 max-h-[min(38vh,260px)] flex flex-col overflow-y-auto overflow-x-hidden " +
  "[scrollbar-width:none] [-ms-overflow-style:none] " +
  "[&::-webkit-scrollbar]:hidden";

/** Same persistence key as before for Communities chevron. */
const COMMUNITIES_SECTION = { id: "communities", title: "Communities", defaultOpen: true };

const FAVORITE_CLUB_SLUGS_KEY = "ah.sidebar.favoriteClubSlugs";

function readFavoriteClubSlugs() {
  try {
    const raw = localStorage.getItem(FAVORITE_CLUB_SLUGS_KEY);
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed)
      ? parsed.map((s) => String(s).trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

function writeFavoriteClubSlugs(slugs) {
  try {
    localStorage.setItem(FAVORITE_CLUB_SLUGS_KEY, JSON.stringify(slugs));
  } catch {
    /* ignore */
  }
}

/**
 * Favorited clubs first: order follows `favoriteSlugsOrdered`, then any remaining
 * favorited clubs in API order, then non-favorited in API order.
 */
function sortJoinedClubsByFavorites(clubs, favoriteSlugsOrdered) {
  const favoriteSet = new Set(favoriteSlugsOrdered);
  const fav = [];
  const favSeen = new Set();
  for (const slug of favoriteSlugsOrdered) {
    const c = clubs.find((x) => clubSlugFromRow(x) === slug);
    if (c) {
      fav.push(c);
      favSeen.add(slug);
    }
  }
  const rest = [];
  for (const c of clubs) {
    const slug = clubSlugFromRow(c);
    if (!slug) {
      rest.push(c);
      continue;
    }
    if (favSeen.has(slug)) continue;
    if (favoriteSet.has(slug)) {
      fav.push(c);
      favSeen.add(slug);
    } else {
      rest.push(c);
    }
  }
  return [...fav, ...rest];
}

function resolveClubMediaUrl(url) {
  if (!url) return null;
  let s = String(url).trim();
  if (!s) return null;
  if (s.startsWith("undefined/")) s = s.replace(/^undefined\/+/, "");
  if (/^https?:\/\//i.test(s)) return s;
  const r2Base = (process.env.REACT_APP_R2_PUBLIC_BASE || "").trim();
  if (r2Base && /^clubs\//i.test(s)) {
    return `${r2Base.replace(/\/+$/, "")}/${s.replace(/^\/+/, "")}`;
  }
  const rooted = s.startsWith("/") ? s : `/${s}`;
  const apiBase = (process.env.REACT_APP_API_BASE || "").trim();
  if (apiBase) return `${apiBase.replace(/\/+$/, "")}${rooted}`;
  return rooted;
}

function clubSlugFromRow(c) {
  const s = (c?.slug || c?.club_slug || "").trim();
  return s || null;
}

function clubAvatarRaw(c) {
  return (
    c?.__draft?.avatar_image_url ||
    c?.avatar_image_url ||
    c?.avatarImageUrl ||
    c?.icon_url ||
    c?.avatar_url ||
    null
  );
}

function Navbar() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { openCreateClubModal } = useCreateClubModal();

  const ROUTES = APP_ROUTES;

  const mainNavItems = useMemo(() => getMainNavItems(), []);

  const collapsibleSections = useMemo(
    () => getCollapsibleSections({ isAuthenticated }),
    [isAuthenticated]
  );

  const mobileNavItems = useMemo(() => getMobilePrimaryTabs(), []);

  const [joinedClubs, setJoinedClubs] = useState([]);
  const [joinedLoading, setJoinedLoading] = useState(false);
  const [favoriteSlugs, setFavoriteSlugs] = useState(readFavoriteClubSlugs);

  const loadJoinedClubs = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setJoinedClubs([]);
      setJoinedLoading(false);
      return;
    }
    setJoinedLoading(true);
    try {
      const data = await fetchMyClubs();
      const list = Array.isArray(data?.clubs) ? data.clubs : [];
      setJoinedClubs(list);
      const valid = new Set(
        list.map((c) => clubSlugFromRow(c)).filter(Boolean)
      );
      setFavoriteSlugs((prev) => {
        const pruned = prev.filter((s) => valid.has(s));
        if (pruned.length === prev.length) return prev;
        writeFavoriteClubSlugs(pruned);
        return pruned;
      });
    } catch {
      setJoinedClubs([]);
    } finally {
      setJoinedLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    loadJoinedClubs();
  }, [loadJoinedClubs]);

  useEffect(() => {
    const onRefresh = () => loadJoinedClubs();
    window.addEventListener("ah:joined-clubs-changed", onRefresh);
    return () => window.removeEventListener("ah:joined-clubs-changed", onRefresh);
  }, [loadJoinedClubs]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (
      location.pathname === APP_ROUTES.clubsDiscover ||
      location.pathname === APP_ROUTES.clubsMy
    ) {
      loadJoinedClubs();
    }
  }, [location.pathname, isAuthenticated, loadJoinedClubs]);

  const sortedJoinedClubs = useMemo(
    () => sortJoinedClubsByFavorites(joinedClubs, favoriteSlugs),
    [joinedClubs, favoriteSlugs]
  );

  const toggleClubFavorite = useCallback((slug) => {
    if (!slug) return;
    setFavoriteSlugs((prev) => {
      const i = prev.indexOf(slug);
      const next = i >= 0 ? prev.filter((s) => s !== slug) : [slug, ...prev];
      writeFavoriteClubSlugs(next);
      return next;
    });
  }, []);

  const [sectionOpen, setSectionOpen] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("ah.nav.sectionState") || "{}");
    } catch {
      return {};
    }
  });

  const sectionIsOpen = useCallback(
    (section) =>
      Object.prototype.hasOwnProperty.call(sectionOpen, section.id)
        ? !!sectionOpen[section.id]
        : section.defaultOpen !== false,
    [sectionOpen]
  );

  const toggleSection = (section) => {
    const current = sectionIsOpen(section);
    const next = { ...sectionOpen, [section.id]: !current };
    setSectionOpen(next);
    try {
      localStorage.setItem("ah.nav.sectionState", JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const isActive = (path) => location.pathname === path;

  const isSectionActive = (base) =>
    location.pathname === base || location.pathname.startsWith(`${base}/`);

  const getProfileImage = () => {
    if (!user?.profile_picture || user.profile_picture.trim() === "") {
      const initials = user?.username?.charAt(0).toUpperCase() || "?";
      return { type: "initials", value: initials };
    }
    if (user.profile_picture.startsWith("http"))
      return { type: "image", value: user.profile_picture };
    if (user.profile_picture.startsWith("/"))
      return { type: "image", value: user.profile_picture };
    return {
      type: "image",
      value: `/${user.profile_picture.replace(/^\/+/, "")}`,
    };
  };

  const profileImage = useMemo(() => getProfileImage(), [user]);

  function DesktopSidebarItem({ to, label, Icon, subtitle, dense = false, isProfile = false }) {
    const active = isActive(to) || isSectionActive(to);

    return (
      <Link
        to={to}
        className={[
          "group relative z-0 flex items-center gap-3 overflow-hidden",
          dense ? "px-2 py-2" : "px-3 py-2.5",
          "border border-transparent transition active:scale-[0.99]",
          active
            ? [
                dense ? "rounded-l-xl" : "rounded-l-2xl",
                "bg-white/[0.035] text-white",
              ].join(" ")
            : dense
              ? "rounded-xl bg-transparent"
              : "rounded-2xl bg-transparent",
          active ? "" : "hover:bg-white/[0.035]",
        ].join(" ")}
        aria-current={active ? "page" : undefined}
      >
        {active ? (
          <>
            <span
              className={[
                "pointer-events-none absolute inset-y-0 right-0 z-[1]",
                dense ? "rounded-l-xl" : "rounded-l-2xl",
                "w-[min(72px,34%)]",
                "bg-[linear-gradient(270deg,rgba(52,211,153,0.09)_0%,rgba(52,211,153,0.022)_42%,transparent_82%)]",
              ].join(" ")}
            />
            <span className="pointer-events-none absolute inset-y-0 right-0 z-[2] w-[3px] bg-emerald-400/90" />
          </>
        ) : null}

        <span
          className={[
            "pointer-events-none absolute inset-0 opacity-0 transition",
            active ? (dense ? "rounded-l-xl" : "rounded-l-2xl") : dense ? "rounded-xl" : "rounded-2xl",
            "bg-[radial-gradient(80%_60%_at_20%_0%,rgba(255,255,255,0.08),transparent_60%)]",
            active ? "" : "group-hover:opacity-100",
          ].join(" ")}
        />

        <span
          className={[
            "relative z-[1] grid shrink-0 place-items-center rounded-xl",
            dense ? "h-8 w-8" : "h-9 w-9",
            "border border-white/10 bg-white/[0.03]",
            "group-hover:border-white/15",
          ].join(" ")}
        >
          {isProfile ? (
            profileImage.type === "image" ? (
              <img
                src={profileImage.value}
                alt="Profile"
                className={[
                  dense ? "h-7 w-7" : "h-8 w-8",
                  "rounded-lg object-cover object-center",
                  active ? "ring-1 ring-white/20" : "ring-1 ring-white/10",
                ].join(" ")}
              />
            ) : (
              <span
                className={[
                  dense ? "h-7 w-7 text-xs" : "h-8 w-8 text-sm",
                  "rounded-lg flex items-center justify-center font-bold text-white",
                  active ? "bg-white/[0.06] ring-1 ring-white/15" : "bg-white/5 ring-1 ring-white/10",
                ].join(" ")}
              >
                {profileImage.value}
              </span>
            )
          ) : (
            <Icon
              size={dense ? 17 : 19}
              className={[
                "transition",
                active ? "text-white/90" : "text-white/60 group-hover:text-white",
              ].join(" ")}
            />
          )}
        </span>

        <span className="relative z-[1] min-w-0 flex-1">
          <div
            className={[
              dense ? "text-[13px]" : "text-[14px]",
              "tracking-tight truncate leading-snug",
              active
                ? "font-medium text-white"
                : "font-medium text-white/60 group-hover:text-white",
            ].join(" ")}
          >
            {label}
          </div>
          {subtitle ? (
            <div className="text-[10px] text-white/38 truncate leading-tight">{subtitle}</div>
          ) : null}
        </span>
      </Link>
    );
  }

  function handleStartCommunity() {
    if (isAuthenticated) {
      openCreateClubModal();
    } else {
      navigate(ROUTES.login, { state: { from: location } });
    }
  }

  function JoinedClubSidebarRow({ club, isFavorite, onToggleFavorite }) {
    const slug = clubSlugFromRow(club);
    const name = club?.name || "Community";
    const to = slug ? `/clubs/${encodeURIComponent(slug)}` : ROUTES.clubsDiscover;
    const avatarSrc = resolveClubMediaUrl(clubAvatarRaw(club));
    const basePath = slug ? `/clubs/${encodeURIComponent(slug)}` : "";
    const rowActive =
      !!basePath &&
      (location.pathname === basePath || location.pathname.startsWith(`${basePath}/`));

    return (
      <div
        className={[
          "group relative flex min-w-0 items-stretch overflow-hidden",
          rowActive ? "rounded-l-xl bg-white/[0.035]" : "rounded-xl",
          "border border-transparent transition",
          rowActive ? "" : "hover:bg-white/[0.035]",
        ].join(" ")}
      >
        {rowActive ? (
          <>
            <span
              className={[
                "pointer-events-none absolute inset-y-0 right-0 z-[1] rounded-l-xl",
                "w-[min(72px,34%)]",
                "bg-[linear-gradient(270deg,rgba(52,211,153,0.09)_0%,rgba(52,211,153,0.022)_42%,transparent_82%)]",
              ].join(" ")}
            />
            <span className="pointer-events-none absolute inset-y-0 right-0 z-[2] w-[3px] bg-emerald-400/90" />
          </>
        ) : null}
        <Link
          to={to}
          className="relative z-[1] flex min-w-0 flex-1 items-center gap-2.5 rounded-l-xl px-2 py-1.5 active:scale-[0.99]"
          aria-current={rowActive ? "page" : undefined}
        >
          <span
            className={[
              "relative grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full",
              "border border-white/10 bg-white/[0.04] group-hover:border-white/15",
            ].join(" ")}
          >
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="h-full w-full object-cover object-center" />
            ) : (
              <span
                className={[
                  "text-[10px] font-bold",
                  rowActive ? "text-white/85" : "text-white/55",
                ].join(" ")}
              >
                {(name || "?").slice(0, 1).toUpperCase()}
              </span>
            )}
          </span>
          <span
            className={[
              "min-w-0 flex-1 truncate text-left text-[13px] leading-snug",
              rowActive
                ? "font-medium text-white"
                : "font-medium text-white/60 group-hover:text-white",
            ].join(" ")}
          >
            {name}
          </span>
        </Link>
        <button
          type="button"
          className={[
            "relative z-[1] grid shrink-0 place-items-center self-center rounded-lg p-1.5",
            "text-white/30 transition hover:bg-white/[0.06] hover:text-white/55",
            isFavorite ? "text-white/55 hover:text-white/70" : "",
          ].join(" ")}
          aria-label={isFavorite ? "Remove from sidebar favorites" : "Favorite in sidebar"}
          aria-pressed={isFavorite}
          title={isFavorite ? "Pinned in sidebar" : "Pin to top of list"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (slug) onToggleFavorite(slug);
          }}
        >
          <Star
            size={15}
            strokeWidth={1.5}
            className={[
              "transition",
              isFavorite
                ? "fill-white/[0.22] text-white/55"
                : "fill-transparent text-white/28 group-hover:text-white/40",
            ].join(" ")}
          />
        </button>
      </div>
    );
  }

  function MobileTab({ to, label, Icon, activeBase }) {
    const active = activeBase
      ? isSectionActive(activeBase)
      : isActive(to) || isSectionActive(to);

    return (
      <Link
        to={to}
        className="flex flex-1 flex-col items-center justify-center py-2"
        aria-current={active ? "page" : undefined}
      >
        <div
          className={[
            "relative grid place-items-center",
            "h-11 w-11 rounded-2xl transition",
            "border border-transparent",
            active
              ? "bg-emerald-300/[0.08] border-emerald-300/20 shadow-[0_0_0_1px_rgba(52,211,153,0.10)]"
              : "bg-white/[0.03] border-white/10",
          ].join(" ")}
        >
          <span
            className={[
              "pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition",
              "bg-[radial-gradient(70%_60%_at_50%_20%,rgba(52,211,153,0.18),transparent_65%)]",
              active ? "opacity-100" : "opacity-0",
            ].join(" ")}
          />

          <Icon
            size={22}
            className={[
              "relative transition",
              active
                ? "text-emerald-200 drop-shadow-[0_0_10px_rgba(52,211,153,0.20)]"
                : "text-white/80",
            ].join(" ")}
          />

          <span
            className={[
              "absolute -bottom-2 left-1/2 -translate-x-1/2",
              "h-1 w-1 rounded-full transition",
              active
                ? "bg-emerald-300/90 shadow-[0_0_10px_rgba(52,211,153,0.35)]"
                : "bg-transparent",
            ].join(" ")}
          />
        </div>

        <span className="sr-only">{label}</span>
      </Link>
    );
  }

  function DesktopMiniNavIcon({ to, label, children }) {
    const active = isSectionActive(to);

    return (
      <Link
        to={to}
        aria-current={active ? "page" : undefined}
        title={label}
        aria-label={label}
        className={[
          "group relative grid place-items-center",
          "h-9 w-9 rounded-2xl transition",
          "text-white/75 hover:text-white/90",
          "active:scale-[0.98]",
        ].join(" ")}
      >
        <span
          className={[
            "pointer-events-none absolute inset-x-2 bottom-1",
            "h-px rounded-full",
            "bg-white/25",
            "opacity-0 transition-opacity",
            "group-hover:opacity-100",
          ].join(" ")}
        />
        <span className={active ? "text-emerald-200" : "text-current"}>{children}</span>
      </Link>
    );
  }

  function MobileMiniNavIcon({ to, label, children }) {
    const active = isSectionActive(to);

    return (
      <Link
        to={to}
        aria-current={active ? "page" : undefined}
        title={label}
        aria-label={label}
        className={[
          "group relative grid place-items-center",
          "h-9 w-9 rounded-2xl transition",
          "text-white/75 hover:text-white/90",
          "active:scale-[0.98]",
        ].join(" ")}
      >
        <span
          className={[
            "pointer-events-none absolute inset-x-2 bottom-1",
            "h-px rounded-full",
            "bg-white/25",
            "opacity-0 transition-opacity",
            "group-hover:opacity-100",
          ].join(" ")}
        />
        <span className={active ? "text-emerald-200" : "text-current"}>{children}</span>
      </Link>
    );
  }

  return (
    <>
      {/* DESKTOP SIDEBAR (lg+) */}
      <aside className="hidden lg:flex fixed left-0 top-0 z-50 h-full w-[252px]">
        <div
          className={[
            "relative h-full w-full",
            "border-r border-white/10",
            "bg-black/55 backdrop-blur-xl",
          ].join(" ")}
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_40%_at_15%_0%,rgba(255,255,255,0.08),transparent_60%)]" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/10 via-black/10 to-black/40" />

          <div className="relative flex h-full w-full flex-col pl-3 pr-0 py-5 min-h-0">
            <div className="w-full shrink-0 px-3">
              <AnimeHouseWordmark
                asLink
                to={ROUTES.home}
                size="md"
                showSubtitle
                linkClassName="flex w-full flex-col items-center justify-center text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/40 focus-visible:ring-offset-2 focus-visible:ring-offset-black/50 rounded-lg px-2"
              />
            </div>

            <div className="mt-5 h-px bg-white/10 shrink-0" />

            <div className={`mt-3 flex min-h-0 flex-1 flex-col ${SCROLL_NO_BAR}`}>
              <nav className="flex flex-col gap-0.5">
                {mainNavItems.map(({ key, to, label, Icon, subtitle }) => (
                  <DesktopSidebarItem
                    key={key}
                    to={to}
                    label={label}
                    Icon={Icon}
                    subtitle={subtitle}
                  />
                ))}
              </nav>

              <div className="my-3 h-px bg-white/10 shrink-0" />

              <div className="flex flex-col gap-0">
                {(() => {
                  const communitiesOpen = sectionIsOpen(COMMUNITIES_SECTION);
                  return (
                    <div>
                      <button
                        type="button"
                        onClick={() => toggleSection(COMMUNITIES_SECTION)}
                        className={[
                          "flex w-full items-center justify-between gap-2",
                          "rounded-lg px-2 py-1.5 text-left transition",
                          "hover:bg-white/[0.04] active:scale-[0.99]",
                        ].join(" ")}
                        aria-expanded={communitiesOpen}
                      >
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">
                          {COMMUNITIES_SECTION.title}
                        </span>
                        <ChevronDown
                          size={14}
                          className={[
                            "shrink-0 text-white/35 transition-transform",
                            communitiesOpen ? "rotate-180" : "rotate-0",
                          ].join(" ")}
                        />
                      </button>
                      {communitiesOpen ? (
                        <nav className="mt-0.5 flex flex-col gap-0 pb-1">
                          <DesktopSidebarItem
                            to={ROUTES.clubsMy}
                            label="Manage communities"
                            Icon={Settings}
                            dense
                          />
                          <button
                            type="button"
                            onClick={handleStartCommunity}
                            className={[
                              "group relative flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition",
                              "border border-transparent",
                              "hover:bg-white/[0.05] hover:border-white/10 active:scale-[0.99]",
                            ].join(" ")}
                            aria-label="Start a community"
                          >
                            <span className="relative grid h-8 w-8 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/[0.03]">
                              <PlusCircle
                                size={17}
                                className="text-white/80 group-hover:text-white/95"
                              />
                            </span>
                            <span className="min-w-0 flex-1 text-[13px] font-medium tracking-tight text-white/80 group-hover:text-white/95">
                              Start a community
                            </span>
                          </button>
                          <div className={`mt-0.5 ${CLUB_LIST_SCROLL}`}>
                            {isAuthenticated && joinedLoading ? (
                              <div className="px-2 py-1.5 text-[11px] text-white/35">Loading…</div>
                            ) : null}
                            {isAuthenticated && !joinedLoading && joinedClubs.length === 0 ? (
                              <div className="px-2 py-1.5 text-[11px] text-white/38">
                                No communities yet
                              </div>
                            ) : null}
                            {!isAuthenticated ? (
                              <div className="px-2 py-1.5 text-[11px] text-white/38">
                                Sign in to see your communities
                              </div>
                            ) : null}
                            {isAuthenticated
                              ? sortedJoinedClubs.map((c, i) => {
                                  const slug = clubSlugFromRow(c);
                                  const fav = slug ? favoriteSlugs.includes(slug) : false;
                                  return (
                                    <JoinedClubSidebarRow
                                      key={String(
                                        c?.club_id ?? c?.id ?? slug ?? `club-${i}`
                                      )}
                                      club={c}
                                      isFavorite={fav}
                                      onToggleFavorite={toggleClubFavorite}
                                    />
                                  );
                                })
                              : null}
                          </div>
                          <DesktopSidebarItem
                            to={ROUTES.clubsDiscover}
                            label="Discover communities"
                            Icon={Compass}
                            dense
                          />
                        </nav>
                      ) : null}
                    </div>
                  );
                })()}

                {collapsibleSections.map((section, idx) => {
                  const open = sectionIsOpen(section);
                  return (
                    <div key={section.id}>
                      <div className="my-2 h-px bg-white/10" />
                      <button
                        type="button"
                        onClick={() => toggleSection(section)}
                        className={[
                          "flex w-full items-center justify-between gap-2",
                          "rounded-lg px-2 py-1.5 text-left transition",
                          "hover:bg-white/[0.04] active:scale-[0.99]",
                        ].join(" ")}
                        aria-expanded={open}
                      >
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/45">
                          {section.title}
                        </span>
                        <ChevronDown
                          size={14}
                          className={[
                            "shrink-0 text-white/35 transition-transform",
                            open ? "rotate-180" : "rotate-0",
                          ].join(" ")}
                        />
                      </button>
                      {open ? (
                        <nav className="mt-0.5 flex flex-col gap-0 pb-1">
                          {section.items.map(({ key, to, label, Icon, subtitle }) => (
                            <DesktopSidebarItem
                              key={key}
                              to={to}
                              label={label}
                              Icon={Icon}
                              subtitle={subtitle}
                              dense
                            />
                          ))}
                        </nav>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-auto pt-3 shrink-0 border-t border-white/10 pr-3">
              <div className="flex items-center justify-end gap-2 px-1 pb-2">
                <DesktopMiniNavIcon to={ROUTES.hallOfFame} label="Hall of Fame">
                  <Trophy
                    size={22}
                    className={[
                      isSectionActive(ROUTES.hallOfFame)
                        ? "text-emerald-200"
                        : "text-white/75",
                    ].join(" ")}
                  />
                </DesktopMiniNavIcon>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <Link
                  to={user ? ROUTES.profile : ROUTES.login}
                  className={[
                    "flex w-full items-center gap-3 rounded-xl transition",
                    "hover:bg-white/[0.06]",
                    "active:scale-[0.99]",
                  ].join(" ")}
                >
                  <div
                    className={[
                      "grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-2xl",
                      "border border-white/12 bg-black/30",
                    ].join(" ")}
                  >
                    {profileImage.type === "image" ? (
                      <img
                        src={profileImage.value}
                        alt=""
                        className="h-full w-full object-cover object-center"
                      />
                    ) : (
                      <span className="text-sm font-semibold text-white/80">
                        {profileImage.value}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-white/90">
                      {user ? user.username : "Guest"}
                    </div>
                    <div className="truncate text-xs text-white/45">
                      {user ? "Your hub" : "Sign in to post"}
                    </div>
                  </div>
                </Link>
              </div>

              <div className="mt-2 text-[11px] text-white/30 px-2">Build: beta</div>
            </div>
          </div>
        </div>
      </aside>

      {/* DESKTOP FLOATING NOTIFICATION (lg+) */}
      <div className="hidden lg:block fixed right-6 top-6 z-[60]">
        <div
          className={[
            "relative",
            "rounded-2xl",
            "h-12 w-12 grid place-items-center",
            "transition",
            "hover:bg-white/[0.04]",
            "active:scale-[0.98]",
            "scale-[1.12]",
          ].join(" ")}
        >
          <div
            className={[
              "pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition",
              "bg-[radial-gradient(70%_70%_at_50%_20%,rgba(52,211,153,0.16),transparent_65%)]",
              "hover:opacity-100",
            ].join(" ")}
          />
          <NotificationBell />
        </div>
      </div>

      {/* MOBILE TOP BAR */}
      <header
        className={[
          "lg:hidden sticky top-0 z-40",
          "border-b border-white/[0.06]",
          "bg-black/30 backdrop-blur-xl",
        ].join(" ")}
        style={{
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_40%_at_10%_0%,rgba(255,255,255,0.06),transparent_60%)]" />

        <div className="relative mx-auto flex h-12 w-full max-w-[760px] items-center justify-between px-4">
          <AnimeHouseWordmark
            asLink
            to={ROUTES.home}
            size="sm"
            showSubtitle
            linkClassName="flex min-w-0 flex-col justify-center leading-tight focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/40 focus-visible:ring-offset-2 focus-visible:ring-offset-black/40 rounded-md"
          />

          <div className="flex items-center gap-2">
            <MobileMiniNavIcon to={ROUTES.hallOfFame} label="Hall of Fame">
              <Trophy
                size={16}
                className={[
                  isSectionActive(ROUTES.hallOfFame)
                    ? "text-emerald-200"
                    : "text-white/75",
                ].join(" ")}
              />
            </MobileMiniNavIcon>

            <div className="relative h-11 w-11 rounded-2xl grid place-items-center">
              <span className="pointer-events-none absolute inset-0 rounded-2xl bg-[radial-gradient(70%_60%_at_50%_20%,rgba(52,211,153,0.10),transparent_65%)] opacity-0 hover:opacity-100 transition" />
              <NotificationBell />
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE BOTTOM TABS */}
      <div
        className={[
          "lg:hidden fixed bottom-0 left-0 right-0 z-50",
          "border-t border-white/10",
          "bg-black/70 backdrop-blur-xl",
        ].join(" ")}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="pointer-events-none h-px w-full bg-white/10" />

        <nav className="mx-auto w-full max-w-[760px] px-2">
          <div className="flex items-center justify-between py-1">
            {mobileNavItems.map(({ key, to, label, Icon, activeBase }) => (
              <MobileTab
                key={key}
                to={to}
                label={label}
                Icon={Icon}
                activeBase={activeBase}
              />
            ))}
          </div>
        </nav>
      </div>

      {/* Spacer so page content never sits under the fixed mobile bar */}
    </>
  );
}

export default Navbar;
